import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ListDocumentsParams,
  UpdateDocumentPatch,
  UploadDocumentMeta,
  UploadDocumentResponse,
} from '@mrrmlab/api-client';
import { useDocumentsApi } from './api-context';

export interface UploadDocumentInput {
  file: File;
  meta?: UploadDocumentMeta;
}

export function useDocumentList(params: ListDocumentsParams) {
  const api = useDocumentsApi();
  return useQuery({
    queryKey: ['documents', 'list', params],
    queryFn: ({ signal }) => api.list(params, signal),
  });
}

export function useDocument(id: number | undefined) {
  const api = useDocumentsApi();
  return useQuery({
    queryKey: ['documents', 'detail', id],
    queryFn: ({ signal }) => api.getOne(id!, signal),
    enabled: id !== undefined,
  });
}

export function usePaperlessTags() {
  const api = useDocumentsApi();
  return useQuery({
    queryKey: ['documents', 'tags'],
    queryFn: ({ signal }) => api.listTags(signal),
    staleTime: 5 * 60_000,
  });
}

export function usePaperlessCorrespondents() {
  const api = useDocumentsApi();
  return useQuery({
    queryKey: ['documents', 'correspondents'],
    queryFn: ({ signal }) => api.listCorrespondents(signal),
    staleTime: 5 * 60_000,
  });
}

export function usePaperlessDocumentTypes() {
  const api = useDocumentsApi();
  return useQuery({
    queryKey: ['documents', 'document-types'],
    queryFn: ({ signal }) => api.listDocumentTypes(signal),
    staleTime: 5 * 60_000,
  });
}

export function useDocumentPreviewBlob(id: number | undefined) {
  const api = useDocumentsApi();
  return useQuery({
    queryKey: ['documents', 'preview', id],
    queryFn: ({ signal }) => api.previewBlob(id!, signal),
    enabled: id !== undefined,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

export function useUploadDocument() {
  const api = useDocumentsApi();
  const qc = useQueryClient();
  return useMutation<UploadDocumentResponse, Error, UploadDocumentInput>({
    mutationFn: ({ file, meta }) => api.upload(file, meta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', 'list'] });
    },
  });
}

export function useUpdateDocument(id: number) {
  const api = useDocumentsApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateDocumentPatch) => api.update(id, patch),
    onSuccess: (doc) => {
      qc.setQueryData(['documents', 'detail', id], doc);
      qc.invalidateQueries({ queryKey: ['documents', 'list'] });
    },
  });
}
